import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchMyCourses } from "@/api/studentAuth";

export default function MyCourses() {
  const navigate = useNavigate();

  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchMyCourses();
        setCourses(data || []);
        
        // حساب الإحصائيات
        calculateStats(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const calculateStats = (coursesData) => {
    const total = coursesData.length;
    const completed = coursesData.filter(c => c.progress === 100).length;
    const inProgress = coursesData.filter(c => c.progress > 0 && c.progress < 100).length;
    const notStarted = coursesData.filter(c => c.progress === 0).length;
    
    setStats({ total, completed, inProgress, notStarted });
  };

  // تصفية الكورسات
  const filteredCourses = courses.filter(course => {
    const matchesSearch = course.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         course.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === "all" ||
                         (filterStatus === "completed" && course.progress === 100) ||
                         (filterStatus === "in-progress" && course.progress > 0 && course.progress < 100) ||
                         (filterStatus === "not-started" && course.progress === 0);
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>جاري تحميل الكورسات...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerContent}>
          <div>
            <h1 style={styles.pageTitle}>كورساتي</h1>
            <p style={styles.pageSubtitle}>إدارة وتصفح جميع الكورسات التي اشتركت بها</p>
          </div>
          <div style={styles.headerStats}>
            <span style={styles.headerStat}>
              <strong>{stats.total}</strong> كورس إجمالي
            </span>
          </div>
        </div>
      </div>

      <div style={styles.content}>
        {/* بطاقة الإحصائيات */}
        <div style={styles.statsCard}>
          <div style={styles.statsGrid}>
            <div style={styles.statItem}>
              <div style={{...styles.statIcon, background: '#4CAF5020', color: '#4CAF50'}}>📚</div>
              <div>
                <h3 style={styles.statNumber}>{stats.total}</h3>
                <p style={styles.statLabel}>إجمالي الكورسات</p>
              </div>
            </div>
            
            <div style={styles.statItem}>
              <div style={{...styles.statIcon, background: '#2196F320', color: '#2196F3'}}>✅</div>
              <div>
                <h3 style={styles.statNumber}>{stats.completed}</h3>
                <p style={styles.statLabel}>مكتملة</p>
              </div>
            </div>
            
            <div style={styles.statItem}>
              <div style={{...styles.statIcon, background: '#FF980020', color: '#FF9800'}}>⏳</div>
              <div>
                <h3 style={styles.statNumber}>{stats.inProgress}</h3>
                <p style={styles.statLabel}>قيد التقدم</p>
              </div>
            </div>
            
            <div style={styles.statItem}>
              <div style={{...styles.statIcon, background: '#9E9E9E20', color: '#9E9E9E'}}>🆕</div>
              <div>
                <h3 style={styles.statNumber}>{stats.notStarted}</h3>
                <p style={styles.statLabel}>لم تبدأ بعد</p>
              </div>
            </div>
          </div>
        </div>

        {/* أدوات التصفية والبحث */}
        <div style={styles.filterCard}>
          <div style={styles.filterGrid}>
            <div style={styles.searchBox}>
              <input
                type="text"
                placeholder="🔍 ابحث عن كورس..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
            </div>
            
            <select 
              value={filterStatus} 
              onChange={(e) => setFilterStatus(e.target.value)}
              style={styles.filterSelect}
            >
              <option value="all">جميع الحالات</option>
              <option value="completed">مكتملة</option>
              <option value="in-progress">قيد التقدم</option>
              <option value="not-started">لم تبدأ بعد</option>
            </select>
            
            <button 
              style={styles.exploreButton}
              onClick={() => navigate("/student/courses/explore")}
            >
              🔍 استكشاف كورسات جديدة
            </button>
          </div>
        </div>

        {/* قائمة الكورسات */}
        {filteredCourses.length === 0 ? (
          <div style={styles.emptyState}>
            {courses.length === 0 ? (
              <>
                <div style={styles.emptyIcon}>📚</div>
                <h3 style={styles.emptyTitle}>لا يوجد كورسات بعد</h3>
                <p style={styles.emptyText}>أنت غير مشترك بأي كورس حتى الآن</p>
                <button 
                  style={styles.exploreEmptyButton}
                  onClick={() => navigate("/student/courses/explore")}
                >
                  استكشاف كورسات جديدة
                </button>
              </>
            ) : (
              <>
                <div style={styles.emptyIcon}>🔍</div>
                <h3 style={styles.emptyTitle}>لا توجد نتائج بحث</h3>
                <p style={styles.emptyText}>جرب مصطلحات بحث أو تصفية مختلفة</p>
                <button 
                  style={styles.clearFiltersButton}
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                  }}
                >
                  مسح الفلاتر
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            <div style={styles.coursesHeader}>
              <h2 style={styles.coursesTitle}>الكورسات الخاصة بي ({filteredCourses.length})</h2>
              <span style={styles.coursesSubtitle}>
                {filteredCourses.length} كورس {searchTerm ? 'مطابق للبحث' : ''}
              </span>
            </div>
            
            <div style={styles.coursesGrid}>
              {filteredCourses.map((course) => (
                <div key={course.id} style={styles.courseCard}>
                  {/* صورة الكورس */}
                  <div style={styles.courseImageContainer}>
                    {course.thumbnail ? (
                      <img
                        src={course.thumbnail}
                        alt={course.title}
                        style={styles.courseImage}
                      />
                    ) : (
                      <div style={styles.courseImagePlaceholder}>
                        <span style={styles.placeholderIcon}>📚</span>
                        <span style={styles.placeholderText}>لا توجد صورة</span>
                      </div>
                    )}
                    <div style={styles.courseBadges}>
                      {course.progress === 100 ? (
                        <span style={{...styles.badge, ...styles.completedBadge}}>✅ مكتمل</span>
                      ) : course.progress > 0 ? (
                        <span style={{...styles.badge, ...styles.inProgressBadge}}>⏳ قيد التقدم</span>
                      ) : (
                        <span style={{...styles.badge, ...styles.notStartedBadge}}>🆕 جديد</span>
                      )}
                      
                      {course.is_featured && (
                        <span style={{...styles.badge, ...styles.featuredBadge}}>⭐ مميز</span>
                      )}
                    </div>
                    
                    {/* شريط التقدم */}
                    <div style={styles.progressContainer}>
                      <div style={styles.progressBar}>
                        <div 
                          style={{
                            ...styles.progressFill,
                            width: `${course.progress || 0}%`,
                            background: course.progress === 100 ? '#4CAF50' : 
                                      (course.progress > 0 ? '#2196F3' : '#9E9E9E')
                          }}
                        ></div>
                      </div>
                      <span style={styles.progressText}>{course.progress || 0}%</span>
                    </div>
                  </div>

                  {/* معلومات الكورس */}
                  <div style={styles.courseContent}>
                    <h3 style={styles.courseTitle}>{course.title}</h3>
                    
                    <p style={styles.courseDescription}>
                      {course.short_description?.substring(0, 100)}
                      {course.short_description?.length > 100 && '...'}
                    </p>
                    
                    <div style={styles.courseMeta}>
                      <div style={styles.metaItem}>
                        <span style={styles.metaIcon}>👨‍🏫</span>
                        <span style={styles.metaText}>{course.trainer || "غير محدد"}</span>
                      </div>
                      
                      <div style={styles.metaItem}>
                        <span style={styles.metaIcon}>🏛️</span>
                        <span style={styles.metaText}>{course.academy || "غير محدد"}</span>
                      </div>
                      
                      <div style={styles.metaItem}>
                        <span style={styles.metaIcon}>📊</span>
                        <span style={styles.metaText}>{course.level || "جميع المستويات"}</span>
                      </div>
                      
                      {course.rating && (
                        <div style={styles.metaItem}>
                          <span style={styles.metaIcon}>⭐</span>
                          <span style={styles.metaText}>{course.rating}/5</span>
                        </div>
                      )}
                    </div>
                    
                    <div style={styles.courseStats}>
                      <span style={styles.stat}>
                        <strong>{course.total_videos || 0}</strong> فيديو
                      </span>
                      <span style={styles.stat}>
                        <strong>{course.total_duration || "0"}</strong> ساعة
                      </span>
                      <span style={styles.stat}>
                        <strong>{course.students_count || 0}</strong> طالب
                      </span>
                    </div>
                  </div>

                  {/* أزرار الإجراءات */}
                  <div style={styles.courseActions}>
                    <button
                      onClick={() => navigate(`/student/courses/${course.id}/watch`)}
                      style={styles.watchButton}
                    >
                      {course.progress === 100 ? "مراجعة الكورس" :
                       course.progress > 0 ? "استكمال المشاهدة" : "بدء المشاهدة"}
                    </button>
                    
                    <div style={styles.secondaryActions}>
                      <button
                        onClick={() => navigate(`/student/courses/${course.id}/details`)}
                        style={styles.detailsButton}
                      >
                        التفاصيل
                      </button>
                      
                      <button
                        onClick={() => navigate(`/student/courses/${course.id}/resources`)}
                        style={styles.resourcesButton}
                      >
                        المواد
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* اقتراحات إضافية */}
        {courses.length > 0 && (
          <div style={styles.suggestionsCard}>
            <h3 style={styles.suggestionsTitle}>اقتراحات لك</h3>
            <p style={styles.suggestionsText}>
              لم تكمل مشاهدة كل الكورسات بعد، استمر في التعلم!
            </p>
            <button 
              style={styles.continueButton}
              onClick={() => {
                const inProgressCourse = courses.find(c => c.progress > 0 && c.progress < 100);
                if (inProgressCourse) {
                  navigate(`/student/courses/${inProgressCourse.id}/watch`);
                }
              }}
            >
              استكمال آخر كورس شاهده
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    direction: 'rtl',
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    background: '#f5f7fa',
    minHeight: '100vh',
  },
  header: {
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    color: 'white',
    padding: '30px',
    borderRadius: '0 0 15px 15px',
    marginBottom: '30px',
  },
  headerContent: {
    maxWidth: '1400px',
    margin: '0 auto',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  pageTitle: {
    fontSize: '2rem',
    margin: '0 0 10px 0',
    fontWeight: '700',
  },
  pageSubtitle: {
    fontSize: '1rem',
    opacity: '0.9',
    margin: 0,
  },
  headerStats: {
    background: 'rgba(255, 255, 255, 0.1)',
    padding: '10px 20px',
    borderRadius: '10px',
    backdropFilter: 'blur(10px)',
  },
  headerStat: {
    fontSize: '1rem',
    fontWeight: '500',
  },
  content: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '0 20px 40px',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '300px',
    color: '#666',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '5px solid #f3f3f3',
    borderTop: '5px solid #4CAF50',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  statsCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    padding: '15px',
    background: '#f8f9fa',
    borderRadius: '10px',
    transition: 'transform 0.3s ease',
  },
  statIcon: {
    fontSize: '2rem',
    width: '60px',
    height: '60px',
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  statNumber: {
    margin: '0 0 5px 0',
    fontSize: '1.8rem',
    color: '#333',
    fontWeight: '700',
  },
  statLabel: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#666',
  },
  filterCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    marginBottom: '30px',
  },
  filterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '15px',
    alignItems: 'center',
  },
  searchBox: {
    gridColumn: 'span 2',
  },
  searchInput: {
    width: '100%',
    padding: '12px 20px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    transition: 'all 0.3s ease',
  },
  filterSelect: {
    padding: '12px 15px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '1rem',
    backgroundColor: 'white',
    fontFamily: 'inherit',
    cursor: 'pointer',
  },
  exploreButton: {
    padding: '12px 25px',
    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '60px 20px',
    textAlign: 'center',
    color: '#777',
  },
  emptyIcon: {
    fontSize: '4rem',
    opacity: '0.5',
    marginBottom: '20px',
  },
  emptyTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#555',
  },
  emptyText: {
    fontSize: '1rem',
    opacity: '0.8',
    marginBottom: '25px',
  },
  exploreEmptyButton: {
    padding: '12px 30px',
    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  clearFiltersButton: {
    padding: '12px 30px',
    background: '#f5f5f5',
    color: '#666',
    border: '1px solid #ddd',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  coursesHeader: {
    marginBottom: '25px',
  },
  coursesTitle: {
    fontSize: '1.5rem',
    margin: '0 0 10px 0',
    color: '#333',
    fontWeight: '600',
  },
  coursesSubtitle: {
    fontSize: '1rem',
    color: '#666',
  },
  coursesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '25px',
    marginBottom: '40px',
  },
  courseCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  },
  courseImageContainer: {
    position: 'relative',
    height: '200px',
  },
  courseImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  courseImagePlaceholder: {
    width: '100%',
    height: '100%',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
  },
  placeholderIcon: {
    fontSize: '3rem',
    marginBottom: '10px',
  },
  placeholderText: {
    fontSize: '0.9rem',
  },
  courseBadges: {
    position: 'absolute',
    top: '15px',
    left: '15px',
    display: 'flex',
    gap: '8px',
  },
  badge: {
    padding: '5px 10px',
    borderRadius: '20px',
    fontSize: '0.75rem',
    fontWeight: '600',
    backdropFilter: 'blur(10px)',
  },
  completedBadge: {
    background: 'rgba(76, 175, 80, 0.9)',
    color: 'white',
  },
  inProgressBadge: {
    background: 'rgba(33, 150, 243, 0.9)',
    color: 'white',
  },
  notStartedBadge: {
    background: 'rgba(158, 158, 158, 0.9)',
    color: 'white',
  },
  featuredBadge: {
    background: 'rgba(255, 193, 7, 0.9)',
    color: '#333',
  },
  progressContainer: {
    position: 'absolute',
    bottom: '0',
    left: '0',
    right: '0',
    background: 'rgba(0, 0, 0, 0.7)',
    padding: '10px 15px',
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  progressBar: {
    flex: 1,
    height: '6px',
    background: 'rgba(255, 255, 255, 0.2)',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: '3px',
    transition: 'width 0.3s ease',
  },
  progressText: {
    color: 'white',
    fontSize: '0.85rem',
    fontWeight: '600',
    minWidth: '35px',
  },
  courseContent: {
    padding: '20px',
  },
  courseTitle: {
    fontSize: '1.2rem',
    margin: '0 0 10px 0',
    color: '#333',
    fontWeight: '600',
    lineHeight: '1.4',
  },
  courseDescription: {
    fontSize: '0.9rem',
    color: '#666',
    margin: '0 0 15px 0',
    lineHeight: '1.5',
  },
  courseMeta: {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '10px',
    marginBottom: '15px',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  metaIcon: {
    fontSize: '0.9rem',
    color: '#4CAF50',
  },
  metaText: {
    fontSize: '0.85rem',
    color: '#666',
  },
  courseStats: {
    display: 'flex',
    justifyContent: 'space-around',
    padding: '15px 0',
    borderTop: '1px solid #f0f0f0',
    borderBottom: '1px solid #f0f0f0',
    marginBottom: '15px',
  },
  stat: {
    textAlign: 'center',
    fontSize: '0.85rem',
    color: '#666',
  },
  courseActions: {
    padding: '0 20px 20px',
  },
  watchButton: {
    width: '100%',
    padding: '12px',
    background: 'linear-gradient(135deg, #4CAF50 0%, #2E7D32 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    marginBottom: '10px',
  },
  secondaryActions: {
    display: 'flex',
    gap: '10px',
  },
  detailsButton: {
    flex: 1,
    padding: '10px',
    background: '#f5f5f5',
    color: '#333',
    border: '1px solid #ddd',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  resourcesButton: {
    flex: 1,
    padding: '10px',
    background: '#e3f2fd',
    color: '#1976d2',
    border: '1px solid #bbdefb',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  suggestionsCard: {
    background: 'white',
    borderRadius: '15px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.08)',
    padding: '25px',
    textAlign: 'center',
  },
  suggestionsTitle: {
    fontSize: '1.3rem',
    margin: '0 0 10px 0',
    color: '#333',
    fontWeight: '600',
  },
  suggestionsText: {
    fontSize: '1rem',
    color: '#666',
    marginBottom: '20px',
  },
  continueButton: {
    padding: '12px 30px',
    background: 'linear-gradient(135deg, #2196F3 0%, #1976D2 100%)',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
};

// إضافة تأثيرات Hover
if (typeof window !== 'undefined') {
  setTimeout(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        to { transform: rotate(360deg); }
      }
      
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      
      .student-input-focus:focus {
        outline: none;
        border-color: #4CAF50 !important;
        box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1) !important;
      }
      
      .explore-btn-hover:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(33, 150, 243, 0.3) !important;
      }
      
      .stat-item-hover:hover {
        transform: translateY(-5px) !important;
        box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1) !important;
      }
      
      .course-card-hover:hover {
        transform: translateY(-10px) !important;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
      }
      
      .watch-btn-hover:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 10px 20px rgba(76, 175, 80, 0.3) !important;
      }
      
      .details-btn-hover:hover {
        background: #e0e0e0 !important;
        border-color: #bbb !important;
      }
      
      .resources-btn-hover:hover {
        background: #bbdefb !important;
        border-color: #90caf9 !important;
      }
      
      .continue-btn-hover:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(33, 150, 243, 0.3) !important;
      }
      
      .explore-empty-btn-hover:hover {
        transform: translateY(-2px);
        box-shadow: 0 10px 20px rgba(33, 150, 243, 0.3) !important;
      }
      
      .clear-filters-btn-hover:hover {
        background: #e0e0e0 !important;
        border-color: #bbb !important;
      }
      
      @media (max-width: 768px) {
        .header-content-responsive {
          flex-direction: column !important;
          text-align: center !important;
          gap: 20px !important;
        }
        
        .stats-grid-responsive {
          grid-template-columns: repeat(2, 1fr) !important;
        }
        
        .filter-grid-responsive {
          grid-template-columns: 1fr !important;
          gap: 10px !important;
        }
        
        .search-box-responsive {
          grid-column: 1 !important;
        }
        
        .courses-grid-responsive {
          grid-template-columns: 1fr !important;
        }
      }
      
      @media (max-width: 480px) {
        .stats-grid-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .course-meta-responsive {
          grid-template-columns: 1fr !important;
        }
        
        .secondary-actions-responsive {
          flex-direction: column !important;
        }
      }
    `;
    document.head.appendChild(style);
    
    // إضافة classes للعناصر
    setTimeout(() => {
      document.querySelectorAll('input').forEach(el => {
        el.className = (el.className || '') + ' student-input-focus';
      });
      document.querySelectorAll('select').forEach(el => {
        el.className = (el.className || '') + ' student-input-focus';
      });
      
      const elements = [
        { selector: '[style*="exploreButton"]', className: 'explore-btn-hover' },
        { selector: '[style*="exploreEmptyButton"]', className: 'explore-empty-btn-hover' },
        { selector: '[style*="clearFiltersButton"]', className: 'clear-filters-btn-hover' },
        { selector: '[style*="statItem"]', className: 'stat-item-hover' },
        { selector: '[style*="courseCard"]', className: 'course-card-hover' },
        { selector: '[style*="watchButton"]', className: 'watch-btn-hover' },
        { selector: '[style*="detailsButton"]', className: 'details-btn-hover' },
        { selector: '[style*="resourcesButton"]', className: 'resources-btn-hover' },
        { selector: '[style*="continueButton"]', className: 'continue-btn-hover' },
      ];
      
      elements.forEach(({ selector, className }) => {
        document.querySelectorAll(selector).forEach(el => {
          el.className = (el.className || '') + ' ' + className;
        });
      });
      
      // Responsive classes
      if (window.innerWidth <= 768) {
        const headerContent = document.querySelector('[style*="headerContent"]');
        const statsGrid = document.querySelector('[style*="statsGrid"]');
        const filterGrid = document.querySelector('[style*="filterGrid"]');
        const searchBox = document.querySelector('[style*="searchBox"]');
        const coursesGrid = document.querySelector('[style*="coursesGrid"]');
        
        if (headerContent) headerContent.className = (headerContent.className || '') + ' header-content-responsive';
        if (statsGrid) statsGrid.className = (statsGrid.className || '') + ' stats-grid-responsive';
        if (filterGrid) filterGrid.className = (filterGrid.className || '') + ' filter-grid-responsive';
        if (searchBox) searchBox.className = (searchBox.className || '') + ' search-box-responsive';
        if (coursesGrid) coursesGrid.className = (coursesGrid.className || '') + ' courses-grid-responsive';
      }
      
      if (window.innerWidth <= 480) {
        const courseMeta = document.querySelectorAll('[style*="courseMeta"]');
        const secondaryActions = document.querySelectorAll('[style*="secondaryActions"]');
        
        courseMeta.forEach(el => {
          el.className = (el.className || '') + ' course-meta-responsive';
        });
        secondaryActions.forEach(el => {
          el.className = (el.className || '') + ' secondary-actions-responsive';
        });
      }
    }, 200);
  }, 100);
}